import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Card } from '../common/Card';
import { Badge } from '../common/Badge';
import { formatCurrency, shortenAddress } from '../../utils/formatters';
import { FishSimple, ChartBar } from '@phosphor-icons/react';

export function WhaleAnalysis({ holders, market, accessStatus }) {
  const { t } = useTranslation();
  const [lastUpdate] = useState(new Date().toLocaleTimeString());
  const isAuthRequired = accessStatus?.isAuthRequired;
  const isMembershipRequired = accessStatus?.isMembershipRequired;

  if (!holders?.data) {
    const emptyState = isAuthRequired
      ? { text: t('auth.loginRequired', '请先登录'), className: 'text-accent' }
      : isMembershipRequired
      ? { text: t('membership.noAccess', '没有会员权限'), className: 'text-accent-warm' }
      : { text: t('detail.noWhaleData'), className: 'text-gray-500' };
    return (
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-accent flex items-center gap-2">
            <span>🐋</span> {t('detail.whaleAnalysis')}
          </h3>
        </div>
        <div className={`${emptyState.className} text-sm text-center py-8`}>
          {emptyState.text}
        </div>
      </Card>
    );
  }

  const {
    yesHolders = [],
    noHolders = [],
    yesTotalAmount = 0,
    noTotalAmount = 0,
    yesTotalShares = 0,
    noTotalShares = 0,
    yesPrice = 0.5,
    noPrice = 0.5,
    whaleConcentration = 0,
    smartMoneyDirection = 'NEUTRAL',
    top10Concentration = 0,
  } = holders.data;

  const totalAmount = yesTotalAmount + noTotalAmount;
  const yesPercent = totalAmount > 0 ? (yesTotalAmount / totalAmount * 100).toFixed(1) : 50;
  const noPercent = totalAmount > 0 ? (noTotalAmount / totalAmount * 100).toFixed(1) : 50;

  // Pie chart data
  const pieData = [
    { name: 'YES', value: parseFloat(yesPercent), color: '#2e9f7a' },
    { name: 'NO', value: parseFloat(noPercent), color: '#d36b6d' },
  ];

  return (
    <Card>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-accent flex items-center gap-2">
          <FishSimple size={20} weight="duotone" /> {t('detail.whaleAnalysis')}
        </h3>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <span>{t('detail.updated')}: {lastUpdate}</span>
          <button className="hover:text-white transition-colors">⟳</button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-surface-highlight/60 rounded-lg p-3">
          <div className="text-xs text-gray-400 mb-1">{t('detail.flowDirection')}</div>
          <div className="flex items-center gap-2">
            <span className={smartMoneyDirection === 'YES' ? 'text-positive' : smartMoneyDirection === 'NO' ? 'text-negative' : 'text-gray-400'}>
              {smartMoneyDirection === 'YES' ? '↗' : smartMoneyDirection === 'NO' ? '↘' : '→'}
            </span>
            <span className="font-bold text-sm">
              {smartMoneyDirection === 'YES' ? market?.teamA : smartMoneyDirection === 'NO' ? market?.teamB : 'NEUTRAL'}
            </span>
          </div>
        </div>
        <div className="bg-surface-highlight/60 rounded-lg p-3">
          <div className="text-xs text-gray-400 mb-1">{t('detail.top10Holdings')}</div>
          <div className="flex items-center gap-2">
            <ChartBar size={16} weight="duotone" className="text-accent" />
            <span className="font-bold">{top10Concentration.toFixed(1)}%</span>
          </div>
        </div>
        <div className="bg-surface-highlight/60 rounded-lg p-3">
          <div className="text-xs text-gray-400 mb-1">{t('detail.top3Concentration')}</div>
          <span className="font-bold">{whaleConcentration.toFixed(1)}%</span>
        </div>
        <div className="bg-surface-highlight/60 rounded-lg p-3">
          <div className="text-xs text-gray-400 mb-1">{t('detail.totalValue')}</div>
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span>💰</span>
              <span className="font-bold">{formatCurrency(totalAmount)}</span>
            </div>
            <div className="text-[10px] text-gray-500">
              {((yesTotalShares + noTotalShares) / 1000).toFixed(1)}k {t('detail.shares')}
            </div>
          </div>
        </div>
      </div>

      {/* Pie Chart */}
      <div className="mb-4">
        <div className="text-sm text-gray-400 mb-2">{t('detail.chipDistribution')}</div>
        <div className="flex items-center gap-4">
          <div className="w-32 h-32">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={30}
                  outerRadius={50}
                  dataKey="value"
                  strokeWidth={0}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => `${value}%`}
                  contentStyle={{ backgroundColor: '#141e28', border: '1px solid rgba(148, 163, 184, 0.12)', borderRadius: 8 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-positive"></span>
                <span className="text-sm">YES</span>
              </span>
              <span className="font-bold text-positive">{yesPercent}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-negative"></span>
                <span className="text-sm">NO</span>
              </span>
              <span className="font-bold text-negative">{noPercent}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Holders Tables */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        {/* YES Holders */}
        <div>
          <div className="text-sm font-medium text-positive mb-2 flex items-center justify-between">
            <span>{market?.teamA?.toUpperCase()} (YES)</span>
            <span className="text-xs text-gray-400">${(market?.priceA * 100 || 0).toFixed(1)}¢</span>
          </div>
          <div className="text-xs text-gray-500 grid grid-cols-3 gap-1 mb-1 px-2">
            <span>{t('detail.rank')}</span>
            <span>{t('detail.holder')}</span>
            <span className="text-right">{t('detail.value')}</span>
          </div>
          <div className="space-y-1 max-h-40 overflow-auto scrollbar-thin">
            {yesHolders.slice(0, 5).map((holder, i) => (
              <div key={i} className="flex items-center text-xs bg-surface-highlight/50 rounded p-2">
                <span className="w-6 text-gray-500">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <span className="w-5 h-5 rounded-full bg-accent-strong flex items-center justify-center text-[10px]">
                      {(holder.pseudonym || 'U')[0].toLowerCase()}
                    </span>
                    <span className="truncate">{holder.pseudonym || shortenAddress(holder.proxyWallet)}</span>
                  </div>
                  <div className="text-gray-500 text-[10px] truncate">{shortenAddress(holder.proxyWallet)}</div>
                </div>
                <div className="text-right">
                  <div className="font-medium text-positive">{formatCurrency(holder.amountUsd || (holder.amount * yesPrice))}</div>
                  <div className="text-gray-500 text-[10px]">{Math.round(holder.amount).toLocaleString()} {t('detail.shares')}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* NO Holders */}
        <div>
          <div className="text-sm font-medium text-negative mb-2 flex items-center justify-between">
            <span>{market?.teamB?.toUpperCase()} (NO)</span>
            <span className="text-xs text-gray-400">${(market?.priceB * 100 || 0).toFixed(1)}¢</span>
          </div>
          <div className="text-xs text-gray-500 grid grid-cols-3 gap-1 mb-1 px-2">
            <span>{t('detail.rank')}</span>
            <span>{t('detail.holder')}</span>
            <span className="text-right">{t('detail.value')}</span>
          </div>
          <div className="space-y-1 max-h-40 overflow-auto scrollbar-thin">
            {noHolders.slice(0, 5).map((holder, i) => (
              <div key={i} className="flex items-center text-xs bg-surface-highlight/50 rounded p-2">
                <span className="w-6 text-gray-500">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <span className="w-5 h-5 rounded-full bg-accent flex items-center justify-center text-[10px]">
                      {(holder.pseudonym || 'U')[0].toLowerCase()}
                    </span>
                    <span className="truncate">{holder.pseudonym || shortenAddress(holder.proxyWallet)}</span>
                  </div>
                  <div className="text-gray-500 text-[10px] truncate">{shortenAddress(holder.proxyWallet)}</div>
                </div>
                <div className="text-right">
                  <div className="font-medium text-negative">{formatCurrency(holder.amountUsd || (holder.amount * noPrice))}</div>
                  <div className="text-gray-500 text-[10px]">{Math.round(holder.amount).toLocaleString()} {t('detail.shares')}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Strategy Insights */}
      <div className="border-t border-glass-border pt-4">
        <h4 className="text-sm font-medium flex items-center gap-2 mb-3">
          <span>✦</span> {t('detail.strategyInsights')}
        </h4>
        <div className="space-y-2 text-sm">
          {whaleConcentration > 80 && (
            <div className="flex items-start gap-2 text-accent-warm">
              <span>•</span>
              <span>⚠️ {t('detail.highConcentrationWarning', { percent: whaleConcentration.toFixed(1) })}</span>
            </div>
          )}
          {smartMoneyDirection !== 'NEUTRAL' && (
            <div className="flex items-start gap-2 text-gray-300">
              <span>•</span>
              <span>{t('detail.flowDirectionInsight', { direction: smartMoneyDirection === 'YES' ? market?.teamA : market?.teamB })}</span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
